import { useState, type ComponentType } from "react";
import {
  AppWindow,
  Boxes,
  CheckCircle2,
  Cloud,
  Database,
  Server,
} from "lucide-react";

type EnvironmentExample = {
  id: string;
  label: string;
  eyebrow: string;
  title: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
  tools: string[];
  workflow: string[];
  result: string;
};

const environments: EnvironmentExample[] = [
  {
    id: "microsoft",
    label: "Microsoft",
    eyebrow: "Microsoft 365 + Power Platform",
    title: "Build on the Microsoft environment your team already knows",
    description: "We connect Microsoft tools into one secure workflow instead of adding another disconnected application.",
    icon: AppWindow,
    tools: ["Power Automate", "Copilot Studio", "Power Apps", "Power BI", "Dataverse"],
    workflow: [
      "A request enters through Outlook, Teams, or a Power App",
      "Power Automate validates, routes, and updates the right records",
      "Copilot Studio answers staff questions while Power BI tracks outcomes",
    ],
    result: "A governed workflow inside the Microsoft ecosystem you already license.",
  },
  {
    id: "google",
    label: "Google",
    eyebrow: "Google Workspace + Google Cloud",
    title: "Turn Workspace activity into structured, searchable operations",
    description: "We automate work across Gmail, Drive, Forms, Sheets, and Google Cloud without forcing teams into a new daily interface.",
    icon: Cloud,
    tools: ["AppSheet", "Apps Script", "Vertex AI", "BigQuery", "Looker Studio"],
    workflow: [
      "A form, email, or document triggers an AppSheet or Apps Script workflow",
      "Vertex AI classifies content and extracts the information your process needs",
      "BigQuery and Looker Studio surface progress, trends, and exceptions",
    ],
    result: "Automation that feels native to Google Workspace and scales into Google Cloud.",
  },
  {
    id: "aws",
    label: "AWS",
    eyebrow: "AWS cloud infrastructure",
    title: "Add secure intelligence to an established AWS environment",
    description: "We design around your existing accounts, data stores, identity controls, and deployment standards.",
    icon: Boxes,
    tools: ["Step Functions", "Lambda", "Amazon Bedrock", "S3", "QuickSight"],
    workflow: [
      "A file or business event starts a Step Functions workflow",
      "Lambda and Amazon Bedrock process, validate, and enrich the data",
      "Results return to your systems and appear in QuickSight reporting",
    ],
    result: "A cloud-native workflow governed inside your existing AWS architecture.",
  },
  {
    id: "enterprise",
    label: "Mixed stack",
    eyebrow: "Salesforce, Oracle, legacy tools + APIs",
    title: "Connect the systems that were never designed to work together",
    description: "Most organizations run a mix of cloud platforms, databases, shared drives, and older line-of-business systems that don't communicate well. We bridge them securely so you don't have to manually manage the data flow.",
    icon: Database,
    tools: ["Salesforce Flow", "Laserfiche", "Tyler Technologies", "Procore", "iManage", "Epic", "Quickbooks", "Ellucian", "Custom APIs"],
    workflow: [
      "An event begins in a CRM, ERP, shared inbox, or legacy database",
      "Secure integrations move the right data through one controlled workflow",
      "Staff receive an action, answer, or dashboard in the system they already use",
    ],
    result: "One connected process across cloud, your documents, and legacy infrastructure, without a temporary solution that doesn't scale.",
  },
  {
    id: "new",
    label: "Starting fresh",
    eyebrow: "No infrastructure yet",
    title: "Start with the environment that fits your security, budget, and growth",
    description: "If you do not have an established platform, we can design the foundation as well as the automation that runs on it.",
    icon: Server,
    tools: ["Cloud foundation", "Managed private environment", "Custom server option", "Identity + backups", "Contextral software"],
    workflow: [
      "Choose a secure cloud foundation in Microsoft Azure, Google Cloud, AWS, or a managed environment",
      "Use a custom server when local control or a specialized requirement makes it the better fit",
      "Deploy Contextral software, integrations, monitoring, access controls, and ongoing support",
    ],
    result: "A complete, right-sized environment with no requirement to choose cloud or custom hardware before we assess the need.",
  },
];

export default function InfrastructureExamples() {
  const [activeId, setActiveId] = useState(environments[0].id);
  const active = environments.find((environment) => environment.id === activeId) ?? environments[0];

  return (
    <section id="environments" className="py-12 sm:py-16 md:py-20 mb-0">
      <div className="text-center mb-8 sm:mb-10">
        <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-3">Automation That Works Where You Work</h2>
        <p className="mx-auto max-w-4xl text-base sm:text-lg text-text-secondary leading-relaxed">
          We use the platforms you already trust, connect mixed environments, or build the right foundation when you are starting from scratch.
        </p>
      </div>

      <div className="overflow-hidden rounded-3xl border border-borderSubtle bg-surface shadow-xl shadow-primary/10">
        <div className="flex gap-2 overflow-x-auto border-b border-borderSubtle bg-surfaceSoft p-3 sm:p-4" role="tablist" aria-label="Workflow environments">
          {environments.map((environment) => {
            const Icon = environment.icon;
            const isActive = environment.id === active.id;
            return (
              <button
                key={environment.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveId(environment.id)}
                className={`inline-flex min-w-max items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
                  isActive
                    ? "bg-primary text-white shadow-md shadow-primary/20"
                    : "border border-transparent text-text-secondary hover:border-borderSubtle hover:bg-surface hover:text-primary"
                }`}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {environment.label}
              </button>
            );
          })}
        </div>

        <div className="grid gap-8 p-6 sm:p-8 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:p-10">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.17em] text-primary mb-2">{active.eyebrow}</p>
            <h3 className="text-2xl sm:text-3xl font-bold text-text-primary leading-tight mb-4">{active.title}</h3>
            <p className="text-text-secondary leading-relaxed mb-6">{active.description}</p>
            <div className="flex flex-wrap gap-2">
              {active.tools.map((tool) => (
                <span key={tool} className="rounded-full border border-primary/15 bg-primary/5 px-3 py-1.5 text-sm font-medium text-primary">
                  {tool}
                </span>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-borderSubtle bg-surfaceSoft p-5 sm:p-6">
            <p className="text-xs font-bold uppercase tracking-[0.17em] text-primary mb-5">Example workflow</p>
            <div className="space-y-3">
              {active.workflow.map((step, index) => (
                <div key={step} className="flex items-start gap-3 rounded-xl border border-borderSubtle bg-surface p-4">
                  <span className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-primary text-xs font-bold text-white">
                    {index + 1}
                  </span>
                  <p className="text-sm sm:text-base text-text-secondary leading-relaxed">{step}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/10 p-4">
              <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-primary" aria-hidden="true" />
              <p className="font-semibold text-text-primary leading-relaxed">{active.result}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
